import { Ionicons } from '@expo/vector-icons';
import { useVideoPlayer, VideoView } from 'expo-video';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { logger } from '@/lib/logger';
import { findCue } from '@/lib/offline/vtt';
import type { CaptionCue } from '@/lib/offline/types';
import type { LessonPlayerRef } from './types';

/**
 * Native (expo-video) player for downloaded lessons. Implements the same
 * imperative surface as the Soundslice players so the lesson screen's
 * chapters / ±10s / resume / up-next logic works unchanged. Captions are drawn
 * by us from the downloaded VTT because expo-video only reads tracks embedded
 * in the media itself. Playback continues in the background with a Now Playing
 * card on the lock screen.
 */
export interface LessonPlayerOfflineProps {
  fileUri: string;
  /** Now Playing card (lock screen / Control Center): title, subtitle, artwork. */
  title?: string;
  subtitle?: string;
  artworkUri?: string | null;
  captionCues: CaptionCue[];
  startSeconds: number;
  videoHeight: number | null;
  onReady: () => void;
  onTimeUpdate: (seconds: number) => void;
  onPlayingChange?: (playing: boolean, playhead: number) => void;
  onEnded?: () => void;
}

const TIME_UPDATE_INTERVAL_S = 0.25;

const LessonPlayerOffline = React.forwardRef<LessonPlayerRef, LessonPlayerOfflineProps>(
  (
    { fileUri, title, subtitle, artworkUri, captionCues, startSeconds, videoHeight, onReady, onTimeUpdate, onPlayingChange, onEnded },
    ref,
  ) => {
    const viewRef = useRef<VideoView>(null);
    const [captionsOn, setCaptionsOn] = useState(true);
    const [cue, setCue] = useState<CaptionCue | null>(null);
    const seekedRef = useRef(false);
    const startSecondsRef = useRef(startSeconds);
    useEffect(() => { startSecondsRef.current = startSeconds; }, [startSeconds]);

    const cbs = useRef({ onReady, onTimeUpdate, onPlayingChange, onEnded });
    useEffect(() => { cbs.current = { onReady, onTimeUpdate, onPlayingChange, onEnded }; });

    const source = useMemo(
      () => ({
        uri: fileUri,
        metadata: {
          title: title || 'Open Studio lesson',
          artist: subtitle || 'Open Studio',
          ...(artworkUri ? { artwork: artworkUri } : {}),
        },
      }),
      [fileUri, title, subtitle, artworkUri],
    );
    const player = useVideoPlayer(source, (p) => {
      p.timeUpdateEventInterval = TIME_UPDATE_INTERVAL_S;
      p.preservesPitch = true;
      // Keep the audio going when the phone locks or the user switches apps,
      // with the standard lock-screen card (title, artwork, play/pause, seek).
      // Needs the `supportsBackgroundPlayback` expo-video plugin option
      // (UIBackgroundModes: audio) and the default `auto` audio mixing mode.
      p.staysActiveInBackground = true;
      p.showNowPlayingNotification = true;
    });

    useEffect(() => {
      seekedRef.current = false;
      setCue(null);
    }, [fileUri]);

    useEffect(() => {
      const status = player.addListener('statusChange', ({ status, error }) => {
        if (status === 'readyToPlay') {
          if (!seekedRef.current) {
            seekedRef.current = true;
            const start = startSecondsRef.current;
            if (start > 0) {
              try { player.currentTime = start; } catch (e) { logger.debug('[offline player] initial seek failed', e); }
            }
          }
          cbs.current.onReady();
        } else if (status === 'error') {
          logger.error('[offline player] playback error', { fileUri, error });
        }
      });
      const time = player.addListener('timeUpdate', ({ currentTime }) => {
        cbs.current.onTimeUpdate(currentTime);
        if (captionCues.length) setCue(findCue(captionCues, currentTime));
      });
      const playing = player.addListener('playingChange', ({ isPlaying }) => {
        cbs.current.onPlayingChange?.(isPlaying, player.currentTime);
      });
      const ended = player.addListener('playToEnd', () => {
        cbs.current.onEnded?.();
      });
      return () => {
        status.remove();
        time.remove();
        playing.remove();
        ended.remove();
      };
    }, [player, captionCues, fileUri]);

    const seekTo = useCallback((seconds: number) => {
      try {
        player.currentTime = Math.max(0, seconds);
      } catch (e) {
        logger.debug('[offline player] seek failed', e);
      }
    }, [player]);

    useImperativeHandle(ref, () => ({
      seekTo,
      pause: () => player.pause(),
      play: () => player.play(),
      togglePlayPause: () => (player.playing ? player.pause() : player.play()),
      toggleFullscreen: () => {
        void viewRef.current?.enterFullscreen().catch((e) => logger.debug('[offline player] fullscreen failed', e));
      },
      // Notation does not exist offline; keep the interface complete.
      setPlayerView: () => {},
    }), [player, seekTo]);

    const hasCaptions = captionCues.length > 0;

    return (
      <View style={videoHeight != null ? { height: videoHeight, width: '100%' } : StyleSheet.absoluteFill}>
        <VideoView
          ref={viewRef}
          player={player}
          style={styles.video}
          nativeControls
          contentFit="contain"
          allowsPictureInPicture
          fullscreenOptions={{ enable: true }}
        />
        {hasCaptions && captionsOn && cue ? (
          <View pointerEvents="none" style={styles.captionWrap}>
            <Text style={styles.captionText}>{cue.text}</Text>
          </View>
        ) : null}
        {hasCaptions ? (
          <Pressable
            onPress={() => setCaptionsOn((v) => !v)}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={captionsOn ? 'Hide captions' : 'Show captions'}
            style={({ pressed }) => [styles.ccButton, pressed && styles.ccButtonPressed, !captionsOn && styles.ccButtonOff]}
          >
            <Ionicons name="chatbox-ellipses-outline" size={16} color="#FFFFFF" />
            <Text style={styles.ccLabel}>CC</Text>
          </Pressable>
        ) : null}
      </View>
    );
  },
);

LessonPlayerOffline.displayName = 'LessonPlayerOffline';
export default React.memo(LessonPlayerOffline);

const styles = StyleSheet.create({
  video: { ...StyleSheet.absoluteFillObject, backgroundColor: '#000' },
  captionWrap: {
    position: 'absolute',
    left: 12,
    right: 12,
    bottom: 56,
    alignItems: 'center',
  },
  captionText: {
    color: '#FFFFFF',
    backgroundColor: 'rgba(0,0,0,0.72)',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 4,
    fontSize: 15,
    lineHeight: 20,
    textAlign: 'center',
    overflow: 'hidden',
  },
  // Top-right: the lesson page's expand button sits top-left.
  ccButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.6)',
  },
  ccButtonPressed: { opacity: 0.7 },
  ccButtonOff: { opacity: 0.5 },
  ccLabel: { color: '#FFFFFF', fontSize: 12, fontWeight: '700', letterSpacing: 0.5 },
});
